
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EditStaffDialog } from '@/components/staff/edit-staff-dialog';
import { DeleteStaffDialog } from '@/components/staff/delete-staff-dialog';
import { AssignUsersDialog } from '@/components/staff/assign-users-dialog';
import { LoginQRDialog } from '@/components/shared/login-qr-dialog';
import {
  MoreHorizontalIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  User,
  ChevronDown,
  ChevronRight,
  UserMinus,
  QrCode,
} from 'lucide-react';
import { useStaff, useRemoveUserFromStaff } from '@/api/staff';
import { useGym } from '@/contexts/gym-context';
import { useToast } from '@/hooks/use-toast';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { TableEmptyRow, TableLoadingRows } from '@/components/shared/table-states';

interface StaffTableProps {
  searchQuery: string;
  onRefresh?: () => void;
}

export function StaffTable({ searchQuery, onRefresh }: StaffTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use states for the staff being edited or deleted
  const [editingStaff, setEditingStaff] = useState<string | null>(null);
  const [deletingStaff, setDeletingStaff] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [assignUsersStaffId, setAssignUsersStaffId] = useState<string | null>(
    null
  );
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [qrStaff, setQrStaff] = useState<{ id: string; name: string } | null>(
    null
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const { selectedGym } = useGym();

  const {
    data: staffMembers,
    isLoading,
    error,
    refetch,
  } = useStaff(selectedGym === 'all' ? undefined : selectedGym);
  const removeUserFromStaff = useRemoveUserFromStaff();

  // Filter staff based on search query with null checks
  const allFilteredStaff = staffMembers
    ? staffMembers.filter(
        (staff) =>
          (staff.name?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          ) ||
          (staff.surname?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          ) ||
          (staff.email?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          ) ||
          (staff.userType?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          )
      )
    : [];

  const totalPages = Math.ceil(allFilteredStaff.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const filteredStaff = allFilteredStaff.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage]);

  const handleRefresh = () => {
    refetch();
    if (onRefresh) onRefresh();
  };

  // Handle staff deletion
  const handleDeleteStaff = useCallback(
    (staff: any) => {
      console.log('Delete staff clicked:', staff);

      // Check if staff is an owner
      if (staff.userType === 'owner') {
        toast({
          title: t('common.error'),
          description: t('staff.cannotDeleteOwner'),
          variant: 'destructive',
        });
        return;
      }

      const staffId = staff._id || staff.id;
      const staffName = `${staff.name || ''} ${staff.surname || ''}`.trim();

      console.log('Opening delete dialog for staff:', staffName, staffId);
      setDeletingStaff({ id: staffId, name: staffName });
    },
    [t, toast]
  );

  const toggleRow = (staffId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [staffId]: !prev[staffId],
    }));
  };

  const handleRemoveUserFromStaff = (staffId: string, userId: string) => {
    removeUserFromStaff.mutate(
      { staffId, userId },
      {
        onSuccess: () => {
          // Optimistically update UI: refetch staff or update local state
          handleRefresh();
        },
      }
    );
  };

  if (error) {
    return (
      <div className="text-center py-4 text-red-500">
        <p>{t('staff.errorLoading')}</p>
        <Button onClick={() => refetch()} variant="outline" className="mt-2">
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead />
            <TableHead>{t('staff.name')}</TableHead>
            <TableHead>{t('staff.userType')}</TableHead>
            <TableHead>{t('staff.email')}</TableHead>
            <TableHead>{t('staff.phone')}</TableHead>
            <TableHead>{t('staff.status')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoadingRows colSpan={7} />
          ) : filteredStaff.length === 0 ? (
            <TableEmptyRow
              colSpan={7}
              message={searchQuery ? t('staff.noSearchResults') : t('staff.noStaff')}
            />
          ) : (
            filteredStaff.map((staff) => {
              const staffId = String(staff._id || staff.id || '');
              return (
                <>
                  <TableRow key={staffId}>
                    <TableCell className="w-8">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleRow(staffId)}
                        aria-label={
                          expandedRows[staffId] ? 'Collapse row' : 'Expand row'
                        }
                      >
                        {expandedRows[staffId] ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="font-medium">{`${staff.name || ''} ${
                      staff.surname || ''
                    }`}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {staff.userType === 'admin'
                          ? t('staff.userTypes.admin')
                          : staff.userType === 'reception'
                          ? t('staff.userTypes.reception')
                          : staff.userType === 'trainer'
                          ? t('staff.userTypes.trainer')
                          : staff.userType === 'owner'
                          ? t('staff.userTypes.owner')
                          : staff.userType}
                      </Badge>
                    </TableCell>
                    <TableCell>{staff.email || ''}</TableCell>
                    <TableCell>{staff.phone || ''}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          staff.status === 'active' ? 'default' : 'secondary'
                        }
                      >
                        {staff.status === 'active'
                          ? t('staff.active')
                          : t('staff.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontalIcon className="h-4 w-4" />
                            <span className="sr-only">
                              {t('common.openMenu')}
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => navigate(`/staff/${staffId}`)}
                          >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            {t('common.view')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setEditingStaff(staffId)}
                          >
                            <PencilIcon className="h-4 w-4 mr-2" />
                            {t('common.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setAssignUsersStaffId(staffId)}
                          >
                            <User className="h-4 w-4 mr-2" />
                            {t('Assign Users')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              setQrStaff({
                                id: staffId,
                                name: `${staff.name} ${staff.surname}`,
                              })
                            }
                          >
                            <QrCode className="h-4 w-4 mr-2" />
                            {t('qrCode.generateLoginQR')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteStaff(staff)}
                          >
                            <TrashIcon className="h-4 w-4 mr-2" />
                            {t('common.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  {expandedRows[staffId] && (
                    <TableRow
                      key={`${staffId}-expanded`}
                      className="bg-muted/50"
                    >
                      <TableCell colSpan={7} className="p-0">
                        <div className="p-4">
                          <h4 className="text-sm font-medium mb-2">
                            {t('planAssignments.assignedUsers')}
                          </h4>
                          {staff.users && staff.users.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>{t('users.name')}</TableHead>
                                  <TableHead>{t('users.email')}</TableHead>
                                  <TableHead>{t('users.status')}</TableHead>
                                  <TableHead className="text-right">
                                    {t('common.actions')}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {staff.users.map((user: any) => (
                                  <TableRow key={user._id || user.id}>
                                    <TableCell>{`${user.name} ${user.surname}`}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                      <Badge
                                        variant={
                                          user.status === 'active'
                                            ? 'default'
                                            : 'secondary'
                                        }
                                      >
                                        {user.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() =>
                                          handleRemoveUserFromStaff(
                                            staffId,
                                            user._id || user.id
                                          )
                                        }
                                        disabled={removeUserFromStaff.isPending}
                                      >
                                        <UserMinus className="h-4 w-4" />
                                        <span className="sr-only">
                                          {t('planAssignments.removeUser')}
                                        </span>
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              {t('planAssignments.noAssignedUsers')}
                            </p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })
          )}
        </TableBody>
      </Table>

      {!isLoading && (
        <DataTablePagination
          page={currentPage}
          pageCount={totalPages}
          total={allFilteredStaff.length}
          pageSize={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={setItemsPerPage}
        />
      )}

      {/* Edit Staff Dialog */}
      {editingStaff && (
        <EditStaffDialog
          staffId={editingStaff}
          open={!!editingStaff}
          onOpenChange={() => setEditingStaff(null)}
          onSuccess={handleRefresh}
        />
      )}

      {/* Delete Staff Dialog */}
      {deletingStaff && (
        <DeleteStaffDialog
          staffId={deletingStaff.id}
          staffName={deletingStaff.name}
          open={!!deletingStaff}
          onOpenChange={(open) => {
            if (!open) setDeletingStaff(null);
          }}
          onSuccess={handleRefresh}
        />
      )}

      {/* Assign Users Dialog */}
      {assignUsersStaffId && (
        <AssignUsersDialog
          staffId={assignUsersStaffId}
          open={!!assignUsersStaffId}
          onOpenChange={(open) => {
            if (!open) setAssignUsersStaffId(null);
          }}
          onSuccess={handleRefresh}
        />
      )}

      {/* QR Code Dialog */}
      {qrStaff && (
        <LoginQRDialog
          userId={qrStaff.id}
          userName={qrStaff.name}
          open={!!qrStaff}
          onOpenChange={(open) => {
            if (!open) setQrStaff(null);
          }}
        />
      )}
    </>
  );
}
